// A player's On account (F-POS-07, spec/06 §8.6): what they owe the organisation — from on-account
// sales and postpaid sessions — their limit and the headroom left, and the three things staff do
// about it. Each action rides its own permission and the server checks again (spec/13 §1):
//
//   Collect     orders:create      a cash or counter-UPI payment into this venue's drawer
//   Write off   credit:write_off   forgive an amount, with a reason, after a confirm step
//   Set limit   credit:manage      the player's own limit (zero stops new on-account sales)
//
// Reading rides `orders:read`. Every route is venue-scoped, so the section shows only in a venue's
// console. Typed amounts become integer minor units through `../money` — never a float (spec/06 §1).

import type { Money, Receivable, ReceivableSettlementTender } from '@clavian/api-client';
import { useTranslation, useVenueFormatter } from '@clavian/i18n/react';
import { Button, SegmentedControl, Skeleton, StatCard, TextField, useToast } from '@clavian/ui-web';
import { useState, type FormEvent, type ReactNode } from 'react';

import { PROBLEM, problemCode } from '../../api/problems';
import { useProblem } from '../../api/use-problem';
import { useAccess } from '../../api/queries';
import { LoadFailure } from '../../shell/LoadFailure';
import { useScopeIds } from '../../shell/scope';
import { Notice, Section, SummaryStrip } from '../../ui';
import { TickingMoney } from '../../ui/ticking';
import { minorToMajor, moneyFrom } from '../money';
import { usePlayerCredit, useSetPlayerCredit, useSettleReceivable, useWriteOffReceivable } from './credit';

const ORDERS_READ = 'orders:read';
const ORDERS_CREATE = 'orders:create';
const CREDIT_WRITE_OFF = 'credit:write_off';
const CREDIT_MANAGE = 'credit:manage';

const TENDERS: readonly ReceivableSettlementTender[] = ['cash', 'counter_upi'];

type Action = 'none' | 'collect' | 'writeOff' | 'limit';

/** A refusal in plain words: more than is owed or a bad amount (named here), otherwise what the catalogue says for its code. */
function useRefusal(): (error: unknown) => string {
  const { t } = useTranslation('console');
  const problem = useProblem();
  return (error) => {
    switch (problemCode(error)) {
      case PROBLEM.conflict:
        return t('players.credit.moreThanOwed');
      case PROBLEM.validationFailed:
        return t('players.credit.amountInvalid');
      default:
        return problem.text(error, { fallback: t('players.credit.failed') });
    }
  };
}

export function CreditSection({ playerId }: { readonly playerId: string }): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const scope = useScopeIds();
  const access = useAccess(scope.access);
  const ready = access.status === 'ready';
  const canRead = ready && access.can(ORDERS_READ) && scope.venueId !== undefined;
  const credit = usePlayerCredit(scope.organizationId, scope.venueId, playerId, canRead);
  const [action, setAction] = useState<Action>('none');

  if (!canRead || scope.venueId === undefined) return null;

  let body: ReactNode;
  if (credit.data === undefined) {
    body = credit.isError ? (
      <LoadFailure error={credit.error} message={t('players.credit.unavailable')} onRetry={() => void credit.refetch()} />
    ) : (
      <Skeleton shape="text" lines={2} />
    );
  } else if (!credit.data.enabled && credit.data.balance.amount === 0) {
    body = (
      <Notice role="note">
        {t('players.credit.off')}
      </Notice>
    );
  } else {
    const position = credit.data;
    const owes = position.balance.amount > 0;
    const canCollect = owes && access.can(ORDERS_CREATE);
    const canWriteOff = owes && access.can(CREDIT_WRITE_OFF);
    const canSetLimit = access.can(CREDIT_MANAGE);
    const done = () => setAction('none');
    body = (
      <>
        {!position.enabled ? <Notice role="note">{t('players.credit.offOwed')}</Notice> : null}
        <SummaryStrip label={t('players.credit.figuresLabel')}>
          <StatCard label={t('players.credit.owed')} value={<TickingMoney className="console-num" value={position.balance} format={(money) => format.money(money)} />} tone={owes ? 'warn' : 'default'} />
          <StatCard label={t('players.credit.limit')} value={<TickingMoney className="console-num" value={position.limit} format={(money) => format.money(money)} />} />
          <StatCard label={t('players.credit.available')} value={<TickingMoney className="console-num" value={position.available} format={(money) => format.money(money)} />} />
        </SummaryStrip>
        {action === 'none' ? (
          canCollect || canWriteOff || canSetLimit ? (
            <div className="console-form__actions">
              {canCollect ? (
                <Button variant="primary" onClick={() => setAction('collect')}>
                  {t('players.credit.collect')}
                </Button>
              ) : null}
              {canWriteOff ? (
                <Button variant="secondary" onClick={() => setAction('writeOff')}>
                  {t('players.credit.writeOff')}
                </Button>
              ) : null}
              {canSetLimit ? (
                <Button variant="ghost" onClick={() => setAction('limit')}>
                  {t('players.credit.setLimit')}
                </Button>
              ) : null}
            </div>
          ) : null
        ) : action === 'collect' ? (
          <CollectForm playerId={playerId} venueId={scope.venueId} position={position} onDone={done} />
        ) : action === 'writeOff' ? (
          <WriteOffForm playerId={playerId} venueId={scope.venueId} position={position} onDone={done} />
        ) : (
          <LimitForm playerId={playerId} venueId={scope.venueId} position={position} onDone={done} />
        )}
      </>
    );
  }

  return (
    <Section title={t('players.credit.title')} description={t('players.credit.hint')}>
      {body}
    </Section>
  );
}

interface FormProps {
  readonly playerId: string;
  readonly venueId: string;
  readonly position: Receivable;
  readonly onDone: () => void;
}

function CollectForm({ playerId, venueId, position, onDone }: FormProps): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const toast = useToast();
  const refusal = useRefusal();
  const scope = useScopeIds();
  const settle = useSettleReceivable(scope.organizationId, venueId, playerId);
  const currency = position.balance.currency;
  const [amount, setAmount] = useState(minorToMajor(position.balance.amount, currency));
  const [tender, setTender] = useState<ReceivableSettlementTender>('cash');
  const [reference, setReference] = useState('');
  const [error, setError] = useState<string | undefined>(undefined);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const paying = moneyFrom(amount, currency);
    if (paying === null || paying.amount <= 0) {
      setError(t('players.credit.amountInvalid'));
      return;
    }
    if (paying.amount > position.balance.amount) {
      setError(t('players.credit.moreThanOwed'));
      return;
    }
    setError(undefined);
    const upiReference = tender === 'counter_upi' ? reference.trim() : '';
    settle.mutate(
      { amount: paying, tender, ...(upiReference === '' ? {} : { reference: upiReference }) },
      {
        onSuccess: (next) => {
          toast.show({ title: t('players.credit.collected', { amount: format.money(paying), owed: format.money(next.balance) }), tone: 'ok' });
          onDone();
        },
        onError: (mutationError) => setError(refusal(mutationError)),
      },
    );
  }

  return (
    <form className="console-form" aria-label={t('players.credit.collect')} noValidate onSubmit={submit}>
      <p className="console-subtle">{t('players.credit.collectHint')}</p>
      {error !== undefined ? (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      ) : null}
      <TextField label={t('players.credit.amount', { currency })} required numeric inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} />
      <SegmentedControl<ReceivableSettlementTender>
        label={t('players.credit.tender.label')}
        value={tender}
        onValueChange={setTender}
        block
        options={TENDERS.map((value) => ({ value, label: t(`players.credit.tender.${value}`) }))}
      />
      {tender === 'counter_upi' ? (
        <TextField label={t('players.credit.reference')} showOptional value={reference} onChange={(event) => setReference(event.target.value)} description={t('players.credit.referenceHint')} />
      ) : null}
      <div className="console-form__actions">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={settle.isPending}>
          {t('players.credit.collectSubmit')}
        </Button>
      </div>
    </form>
  );
}

function WriteOffForm({ playerId, venueId, position, onDone }: FormProps): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const toast = useToast();
  const refusal = useRefusal();
  const scope = useScopeIds();
  const writeOff = useWriteOffReceivable(scope.organizationId, venueId, playerId);
  const currency = position.balance.currency;
  const [amount, setAmount] = useState(minorToMajor(position.balance.amount, currency));
  const [reason, setReason] = useState('');
  const [errors, setErrors] = useState<{ amount?: string; reason?: string }>({});
  const [failure, setFailure] = useState<string | undefined>(undefined);
  // The confirm step: writing off forgives real money, so the amount is read back before it goes.
  const [confirming, setConfirming] = useState<Money | null>(null);

  function review(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const next: { amount?: string; reason?: string } = {};
    const forgiving = moneyFrom(amount, currency);
    if (forgiving === null || forgiving.amount <= 0) next.amount = t('players.credit.amountInvalid');
    else if (forgiving.amount > position.balance.amount) next.amount = t('players.credit.moreThanOwed');
    if (reason.trim() === '') next.reason = t('players.credit.reasonRequired');
    setErrors(next);
    setFailure(undefined);
    if (Object.keys(next).length > 0 || forgiving === null) return;
    setConfirming(forgiving);
  }

  function confirm(forgiving: Money): void {
    writeOff.mutate(
      { amount: forgiving, reason: reason.trim() },
      {
        onSuccess: (next) => {
          toast.show({ title: t('players.credit.writtenOff', { amount: format.money(forgiving), owed: format.money(next.balance) }), tone: 'ok' });
          onDone();
        },
        onError: (mutationError) => {
          setFailure(refusal(mutationError));
          setConfirming(null);
        },
      },
    );
  }

  if (confirming !== null) {
    const shown = format.money(confirming);
    return (
      <div className="console-form">
        <Notice tone="warn" role="alert" title={t('players.credit.writeOffConfirmTitle', { amount: shown })}>
          {t('players.credit.writeOffConfirm', { amount: shown })}
        </Notice>
        <div className="console-form__actions">
          <Button type="button" variant="ghost" onClick={() => setConfirming(null)}>
            {t('common.back')}
          </Button>
          <Button type="button" variant="danger" busy={writeOff.isPending} onClick={() => confirm(confirming)}>
            {t('players.credit.writeOffSubmit', { amount: shown })}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <form className="console-form" aria-label={t('players.credit.writeOff')} noValidate onSubmit={review}>
      <Notice tone="warn" role="note">
        {t('players.credit.writeOffHint')}
      </Notice>
      {failure !== undefined ? (
        <Notice tone="danger" role="alert">
          {failure}
        </Notice>
      ) : null}
      <TextField label={t('players.credit.amount', { currency })} required numeric inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} error={errors.amount} />
      <TextField label={t('players.credit.reasonLabel')} description={t('players.credit.reasonHint')} required value={reason} onChange={(event) => setReason(event.target.value)} error={errors.reason} />
      <div className="console-form__actions">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="danger">
          {t('players.credit.writeOffReview')}
        </Button>
      </div>
    </form>
  );
}

function LimitForm({ playerId, venueId, position, onDone }: FormProps): ReactNode {
  const { t } = useTranslation('console');
  const format = useVenueFormatter();
  const toast = useToast();
  const refusal = useRefusal();
  const scope = useScopeIds();
  const setLimit = useSetPlayerCredit(scope.organizationId, venueId, playerId);
  const currency = position.limit.currency;
  const [amount, setAmount] = useState(minorToMajor(position.limit.amount, currency));
  const [error, setError] = useState<string | undefined>(undefined);

  function submit(event: FormEvent<HTMLFormElement>): void {
    event.preventDefault();
    const limit = moneyFrom(amount, currency);
    if (limit === null) {
      setError(t('players.credit.limitInvalid'));
      return;
    }
    setError(undefined);
    setLimit.mutate(
      { limit },
      {
        onSuccess: (next) => {
          toast.show({ title: t('players.credit.limitSaved', { amount: format.money(next.limit) }), tone: 'ok' });
          onDone();
        },
        onError: (mutationError) => setError(problemCode(mutationError) === PROBLEM.validationFailed ? t('players.credit.limitInvalid') : refusal(mutationError)),
      },
    );
  }

  return (
    <form className="console-form" aria-label={t('players.credit.setLimit')} noValidate onSubmit={submit}>
      <p className="console-subtle">{t('players.credit.limitHint')}</p>
      <TextField label={t('players.credit.amount', { currency })} required numeric inputMode="decimal" value={amount} onChange={(event) => setAmount(event.target.value)} error={error} />
      <div className="console-form__actions">
        <Button type="button" variant="ghost" onClick={onDone}>
          {t('common.cancel')}
        </Button>
        <Button type="submit" variant="primary" busy={setLimit.isPending}>
          {t('players.credit.limitSubmit')}
        </Button>
      </div>
    </form>
  );
}
